import type { AxiosError } from 'axios';
import axios from 'axios';
import { ChatFooter } from 'Components/Chat/ChatFooter';
import { ChatLayoutView } from 'Components/Chat/ChatLayout/ChatLayoutView';
import { ChatLayoutContext } from 'Components/Chat/contexts/ChatLayoutContext';
import { MESSAGE_LOAD_LIMIT } from 'Constants/env';
import ContextContentContainer from 'Containers/ContextContent';
import ContextHeaderContainer from 'Containers/ContextHeader';
import { usePreferences } from 'Hooks/preferences/usePreferences';
import { MobXProviderContext, observer } from 'mobx-react';
import { ConversationEmptyState } from 'Modules/conversation/components/ConversationEmptyState';
import { useConversationHasMessages } from 'Modules/conversation/hooks/useConversationHasMessages';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import { fetchOptOutStatus } from 'Modules/conversation/store/opt-out/index.requests';
import React, { useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { isNullOrUndefined } from 'Utils/typeGuards';

export const ChatLayout: React.FC = observer(() => {
  const { conversation, person, notification, message } =
    React.useContext<RootStoreProps>(MobXProviderContext);
  const { view } = React.useContext(ChatLayoutContext);
  const { preferences } = usePreferences();
  const [renderChat, setRenderChat] = React.useState(false);
  const [messagesLoadFailed, setMessagesLoadFailed] = React.useState(false);
  const otherParticipantsRef = useRef<boolean>(false);
  const navigate = useNavigate();

  const { conversationId } = useParams();

  const isSharedFilesAndLinksView = view === 'shared-files-and-links';

  React.useLayoutEffect(() => {
    setRenderChat(false);
  }, [conversation.CurrentConversation?.id, person.loggedInPersonId]);

  React.useEffect(() => {
    void (async () => {
      const response = await conversation
        .loadConversationByIdIfMissingGet(conversationId)
        .catch((err: AxiosError) => {
          const resStatus = err.response.status;
          if (resStatus === 403 || resStatus === 404) {
            navigate('/chat');
          } else throw new Error(err.message);
        });

      if (response && response.data) {
        const currentConversation = conversation?.CurrentConversation;
        setMessagesLoadFailed(false);
        // Load messages before checking hasMessages, since lastMessageId can remain
        // set after history is deleted and cannot reliably determine if the chat is empty
        try {
          await person.waitUntilLoggedIn();
          await message.loadConversationMessagesIfMissingGet(conversationId, {
            Limit: MESSAGE_LOAD_LIMIT,
            SortDirection: 'Descending',
            ShowDeleted: true,
            ShowCallMessagesInChat: preferences.showCallMessagesInChat,
          });
        } catch {
          setMessagesLoadFailed(true);
        }

        if (
          ['Channel', 'Group', 'OneOnOne'].includes(
            currentConversation?.grouping
          )
        ) {
          const otherSmsParticipants = currentConversation.participants.filter(
            ({ personId, phone }) =>
              personId !== person.loggedInPersonId && phone
          );

          const otherParticipants = currentConversation.participants.filter(
            ({ personId }) => personId !== person.loggedInPersonId
          );

          otherParticipantsRef.current = otherParticipants.length > 0;

          if (otherSmsParticipants.length > 0) {
            try {
              // Select the participants who don't already have opt-out status
              const otherParticipantsPhoneNumbers = otherSmsParticipants
                .filter(({ phone }) =>
                  isNullOrUndefined(
                    useConversationStore
                      .getState()
                      .getOptOutPhoneNumberInfo(phone)
                  )
                )
                .map(({ phone }) => phone);

              await fetchOptOutStatus(otherParticipantsPhoneNumbers);
            } catch (error) {
              if (
                axios.isAxiosError(error) &&
                error.response &&
                error.response.status === 404
              ) {
                // 404 is expected when 10DLC is not enabled
                // We don't want to show a notification for this
              } else {
                notification.addAxiosErrorNotification(
                  error,
                  'Error fetching opt out status'
                );
              }
            }

            setRenderChat(true);
            return;
          }
        }

        setRenderChat(true);
      }
    })();

    return () => {
      setRenderChat(false);
    };
  }, [
    conversation,
    conversation?.CurrentConversation?.id,
    conversationId,
    message,
    navigate,
    notification,
    person,
    person.loggedInPersonId,
    preferences.showCallMessagesInChat,
  ]);

  const { hasMessages: hasLoadedMessages } =
    useConversationHasMessages(conversationId);

  // If the load failed, trust lastMessageId so we don't show a false empty state
  const hasMessages =
    hasLoadedMessages ||
    (messagesLoadFailed && !!conversation.CurrentConversation?.lastMessageId);

  return renderChat ? (
    <>
      {!isSharedFilesAndLinksView &&
        <ContextHeaderContainer />
      }
      {isSharedFilesAndLinksView ? (
        <ChatLayoutView />
      ) : hasMessages ? (
        <ContextContentContainer />
      ) : (
        <ConversationEmptyState
          isContactSelected={otherParticipantsRef.current}
          className="mt-6"
        />
      )}
      {!isSharedFilesAndLinksView &&
        <ChatFooter />
      }
    </>
  ) : null;
});
